import { z } from 'zod';
import { CSCU, MAX_AMOUNT, fmtSCU } from './quantity.ts';
import { People, Quality, RecordId } from './schemas.ts';

// Refinery jobs (plan 2B Assumptions 8 and 9, R12–R17): their shapes, and Sierra's
// rules as pure functions, ported from sierra-panel.html at the lines given.
// tests/refinery-rules.test.ts holds the plan's tables.

export const JobScope = z.enum(['org', 'personal']);
export type JobScope = z.infer<typeof JobScope>;

/** What's stored. Refining or Ready by time is worked out from `endsAt` (R13). */
export const JobStatus = z.enum(['refining', 'ready', 'picked_up', 'cancelled']);
export type JobStatus = z.infer<typeof JobStatus>;

/** One material in a job. */
export const JobLine = z.object({
  id: z.string(),
  /** The material's name, as typed or from its item. */
  name: z.string(),
  /** A Materials item: always for an org job, never needed for a personal one. */
  item: z.string().nullable(),
  quality: z.number().int().nullable(),
  /** Raw SCU going in, in thousandths, or null when not given. */
  raw: z.number().int().nullable(),
  /** The unrefined lot the ore came from (org jobs only). */
  source: z.string().nullable(),
  /** cSCU picked up: null until picked up; 0 means nothing came out. */
  picked: z.number().int().nullable(),
});
export type JobLine = z.infer<typeof JobLine>;

export const Job = z.object({
  id: z.string(),
  scope: JobScope,
  refiner: z.string(),
  location: z.string(),
  method: z.string(),
  /** aUEC, recorded only. */
  cost: z.number().int(),
  startedAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  status: JobStatus,
  lines: z.array(JobLine),
  pickedUpAt: z.iso.datetime().nullable(),
  pickedUpBy: z.string().nullable(),
  storedAt: z.string(),
  version: z.number().int(),
  created: z.iso.datetime(),
});
export type Job = z.infer<typeof Job>;

export const Preset = z.object({ id: z.string(), location: z.string(), method: z.string(), minutes: z.number().int() });
export type Preset = z.infer<typeof Preset>;

const LineInput = z.object({
  /** Kept on an edit, so a line's pickup and history stay its own. */
  id: z.string().max(32).nullable().default(null),
  name: z.string().trim().max(80).default(''),
  quality: Quality.default(null),
  raw: z.number().int().min(1).max(MAX_AMOUNT).nullable().default(null),
  source: RecordId.nullable().default(null),
});
export type LineInput = z.output<typeof LineInput>;

const Duration = z.object({ hours: z.number().int().min(0).max(999).default(0), minutes: z.number().int().min(0).max(59_999).default(0) });

export const JobInput = z.object({
  scope: JobScope,
  /** Org jobs: a quartermaster may name someone else with supply.refine. Personal jobs are always their creator's. */
  refiner: RecordId.nullable().default(null),
  location: z.string().trim().max(64).default(''),
  method: z.string().trim().max(64).default(''),
  cost: z.number().int().min(0).max(MAX_AMOUNT).default(0),
  duration: Duration,
  lines: z.array(LineInput).min(1).max(20),
  /** Save this refinery, method and time as a preset (supply.refine only). */
  savePreset: z.boolean().default(false),
});

export const JobUpdate = z.object({
  refiner: RecordId.nullable().default(null),
  location: z.string().trim().max(64).default(''),
  method: z.string().trim().max(64).default(''),
  cost: z.number().int().min(0).max(MAX_AMOUNT).default(0),
  /** Time left from now; 0 makes it Ready at once, with no notification. */
  left: Duration,
  lines: z.array(LineInput).min(1).max(20),
  version: z.number().int().min(1),
});

export const PickupInput = z.object({
  /** cSCU picked up per line id; null or missing means not picked up. */
  picked: z.record(z.string(), z.number().int().min(0).max(MAX_AMOUNT / CSCU).nullable()),
  /** Org jobs: the stockpile location the output goes to; the refinery by default. */
  storedAt: z.string().trim().max(64).default(''),
});

export const RefineryView = z.object({
  /** Your personal jobs and the org jobs you refine. */
  mine: z.array(Job),
  /** Org jobs, for supply.refine and supply.view; empty otherwise. */
  org: z.array(Job),
  presets: z.array(Preset),
  people: People,
  /** Open org jobs whose refiner has lost access. */
  refinerLeft: z.array(z.string()),
});
export type RefineryView = z.infer<typeof RefineryView>;

/** A job as R13 sees it. */
export type JobState = 'refining' | 'ready' | 'picked_up' | 'cancelled';

/** R13 (`jobState`, line 2395): picked up or cancelled if so; Ready if marked ready or its time has passed; otherwise Refining. */
export function jobState(job: Pick<Job, 'status' | 'endsAt'>, now: number): JobState {
  if (job.status === 'picked_up' || job.status === 'cancelled') return job.status;
  return job.status === 'ready' || new Date(job.endsAt).getTime() <= now ? 'ready' : 'refining';
}

/** Whether a job is still open: refining, or ready and not picked up. */
export function isOpen(job: Pick<Job, 'status'>): boolean {
  return job.status === 'refining' || job.status === 'ready';
}

const MINUTE = 60_000;

/** R12: a duration in ms, or null when it's nothing. Minutes past 59 carry: 1 h 90 m is 150 minutes. */
export function durationMs(duration: { hours: number; minutes: number }): number | null {
  const ms = (duration.hours * 60 + duration.minutes) * MINUTE;
  return ms > 0 ? ms : null;
}

/** R12 (line 2379): a job re-timed to `left` ms from now. Its start never passes its end. */
export function retime(now: number, startedAt: number, left: number): { startedAt: number; endsAt: number } {
  const endsAt = now + Math.max(0, left);
  return { startedAt: Math.min(startedAt, endsAt - 1), endsAt };
}

/** R12 (`tmr-plus`, line 2921): 15 more minutes, from its end, or from now if that's passed. */
export function extended(now: number, endsAt: number): number {
  return Math.max(now, endsAt) + 15 * MINUTE;
}

/** R14: the thousandths of an SCU a line's cSCU makes. */
export function yieldOf(cscu: number): number {
  return cscu * CSCU;
}

/** R16 (line 2379): `Refining: Quantanium Q600, Gold, Iron +1 · 12 SCU`. */
export function jobLabel(lines: readonly Pick<JobLine, 'name' | 'quality' | 'raw'>[]): string {
  const names = lines.map((line) => `${line.name}${line.quality === null ? '' : ` Q${String(line.quality)}`}`);
  const raw = lines.reduce((sum, line) => sum + (line.raw ?? 0), 0);
  return `Refining: ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${String(names.length - 3)}` : ''}${raw > 0 ? ` · ${fmtSCU(raw)} SCU` : ''}`;
}

/** The most presets the org keeps. */
export const MAX_PRESETS = 12;

/** R17 (line 2383): a saved preset goes first, replacing one with the same refinery and method (ignoring case); 12 at most. */
export function savePreset<P extends { location: string; method: string }>(list: readonly P[], preset: P): P[] {
  const same = (a: P) => a.location.trim().toLowerCase() === preset.location.trim().toLowerCase() && a.method.trim().toLowerCase() === preset.method.trim().toLowerCase();
  return [preset, ...list.filter((entry) => !same(entry))].slice(0, MAX_PRESETS);
}

/** How far through its time a job is, 0 to 100. */
export function progress(job: Pick<Job, 'startedAt' | 'endsAt'>, now: number): number {
  const start = new Date(job.startedAt).getTime();
  const end = new Date(job.endsAt).getTime();
  if (end <= start) return 100;
  return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
}
